/**
 * MachineTypeDetailPage - Detailansicht/Neuanlage eines Maschinentyps
 * 
 * Sektionen:
 * - Grunddaten (Name)
 * - Technisch (Code - read-only, auto-generiert)
 * - Hinweise
 * - Gefahrenzone (Deaktivieren)
 * 
 * UX-Leitlinie: Code ist technisch, nicht User-relevant
 */

import { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Cog, ArrowLeft, RefreshCw, Save, Trash2, AlertTriangle, Copy, ChevronDown, ChevronRight } from 'lucide-react';
import { AdminLayout } from '../../components/AdminLayout';

interface MachineType {
  id: string;
  code: string;
  name: string;
  isActive: boolean;
}

interface FormData {
  name: string;
}

const INITIAL_FORM: FormData = {
  name: '',
};

export default function MachineTypeDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const isNew = id === 'new';
  
  const [machineType, setMachineType] = useState<MachineType | null>(null);
  const [loading, setLoading] = useState(!isNew);
  const [formData, setFormData] = useState<FormData>(INITIAL_FORM);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  // Technical section collapsed state
  const [techOpen, setTechOpen] = useState(false);
  
  // Delete Confirm State
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);

  // Load machine type details (only for edit mode)
  const loadMachineType = async () => {
    if (isNew || !id) return;
    setLoading(true);
    try {
      const res = await fetch(`/api/admin/machine-types/${id}`, { credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        setMachineType(data);
        setFormData({
          name: data.name,
        });
      } else if (res.status === 404) {
        navigate('/admin/machine-types');
      }
    } catch (err) {
      console.error('Fehler beim Laden des Maschinentyps:', err);
    }
    setLoading(false);
  };

  useEffect(() => {
    if (!isNew) {
      loadMachineType();
    }
  }, [id, isNew]);

  // Copy to clipboard
  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
  };

  // Save (create or update)
  const handleSave = async () => {
    if (!formData.name.trim()) {
      setError('Name ist erforderlich');
      return;
    }

    setSaving(true);
    setError(null);

    try {
      const url = isNew ? '/api/admin/machine-types' : `/api/admin/machine-types/${id}`;
      const res = await fetch(url, {
        method: isNew ? 'POST' : 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          name: formData.name.trim(),
        }),
      });

      if (res.ok) {
        const data = await res.json();
        if (isNew) {
          navigate(`/admin/machine-types/${data.id}`);
        } else {
          loadMachineType();
        }
      } else {
        const data = await res.json().catch(() => ({}));
        setError(data.error || data.message || 'Fehler beim Speichern');
      }
    } catch (err) {
      setError('Netzwerkfehler');
    }
    setSaving(false);
  };

  // Delete (soft delete)
  const handleDelete = async () => {
    if (!id || isNew) return;
    
    setDeleting(true);
    try {
      const res = await fetch(`/api/admin/machine-types/${id}`, {
        method: 'DELETE',
        credentials: 'include',
      });
      
      if (res.ok) {
        navigate('/admin/machine-types');
      }
    } catch (err) {
      console.error('Fehler beim Deaktivieren:', err);
    }
    setDeleting(false);
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="p-8 text-center text-slate-500">Laden...</div>
      </AdminLayout>
    );
  }

  if (!isNew && !machineType) {
    return (
      <AdminLayout>
        <div className="p-8 text-center text-slate-500">Maschinentyp nicht gefunden</div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="max-w-5xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div>
            <Link 
              to="/admin/machine-types" 
              className="text-sm text-slate-500 hover:text-slate-700 flex items-center gap-1 mb-2"
            >
              <ArrowLeft className="w-4 h-4" />
              Zurück zur Übersicht
            </Link>
            <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
              <Cog className="w-6 h-6" />
              {isNew ? 'Neuer Maschinentyp' : machineType?.name}
              {!isNew && machineType && (
                <span className="text-sm font-mono text-slate-400">({machineType.code})</span>
              )}
            </h1>
            {!isNew && machineType && !machineType.isActive && (
              <p className="text-sm text-red-500 mt-1">(inaktiv)</p>
            )}
          </div>
          <div className="flex gap-2">
            {!isNew && (
              <button
                onClick={() => loadMachineType()}
                disabled={loading}
                className="p-2 border border-slate-300 rounded-lg hover:bg-slate-50 disabled:opacity-50"
                title="Aktualisieren"
              >
                <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
              </button>
            )}
            <button
              onClick={handleSave}
              disabled={saving || !formData.name.trim()}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center gap-2"
            >
              <Save className="w-4 h-4" />
              {saving ? 'Speichern...' : 'Speichern'}
            </button>
          </div>
        </div>

        {/* Error */}
        {error && (
          <div className="p-4 bg-red-50 border border-red-200 text-red-700 rounded-lg">
            {error}
          </div>
        )}

        {/* Form Sections */}
        <div className="grid lg:grid-cols-2 gap-6">
          {/* Grunddaten */}
          <section className="bg-white rounded-lg border p-6 space-y-4">
            <h2 className="font-semibold text-slate-900 border-b pb-2">Grunddaten</h2>
            
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="z.B. Waschmaschine"
                className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                autoFocus
              />
            </div>
            
          </section>

          {/* Hinweise */}
          <section className="bg-slate-50 rounded-lg border p-6 space-y-4">
            <h2 className="font-semibold text-slate-700">Hinweise</h2>
            <p className="text-sm text-slate-600">
              Maschinentypen kategorisieren Maschinen für eine bessere Übersicht und Filterung. 
              Das Deaktivieren eines Typs beeinflusst bereits erfasste Daten nicht, 
              da diese Snapshot-Informationen enthalten.
            </p>
          </section>

          {/* Technisch (nur bei Edit) */}
          {!isNew && machineType && (
            <section className="bg-white rounded-lg border p-4 lg:col-span-2">
              <button
                onClick={() => setTechOpen(!techOpen)}
                className="w-full flex items-center justify-between text-left"
              >
                <h2 className="font-medium text-slate-500 text-sm">Technische Details</h2>
                {techOpen ? <ChevronDown className="w-4 h-4 text-slate-400" /> : <ChevronRight className="w-4 h-4 text-slate-400" />}
              </button>
              {techOpen && (
                <div className="mt-3 pt-3 border-t space-y-2">
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-slate-500">Code</span>
                    <div className="flex items-center gap-2">
                      <code className="text-sm font-mono bg-slate-100 px-2 py-1 rounded">{machineType.code}</code>
                      <button
                        onClick={() => copyToClipboard(machineType.code)}
                        className="p-1 text-slate-400 hover:text-slate-600 rounded"
                        title="Code kopieren"
                      >
                        <Copy className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-slate-500">ID</span>
                    <div className="flex items-center gap-2">
                      <code className="text-sm font-mono bg-slate-100 px-2 py-1 rounded text-xs">{machineType.id}</code>
                      <button
                        onClick={() => copyToClipboard(machineType.id)}
                        className="p-1 text-slate-400 hover:text-slate-600 rounded"
                        title="ID kopieren"
                      >
                        <Copy className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </section>
          )}
        </div>

        {/* Delete Section (nur bei Edit) */}
        {!isNew && machineType && (
          <section className="bg-white rounded-lg border border-red-200 p-6">
            <h2 className="font-semibold text-red-600 mb-2">Gefahrenzone</h2>
            <p className="text-sm text-slate-600 mb-4">
              Das Deaktivieren eines Maschinentyps entfernt ihn aus der Auswahl. 
              Bereits zugeordnete Maschinen behalten ihre Typ-Information.
            </p>
            <button
              onClick={() => setDeleteConfirmOpen(true)}
              className="px-4 py-2 border border-red-300 text-red-600 rounded-lg hover:bg-red-50 flex items-center gap-2"
            >
              <Trash2 className="w-4 h-4" />
              Maschinentyp deaktivieren
            </button>
          </section>
        )}
      </div>

      {/* Delete Confirm Modal */}
      {deleteConfirmOpen && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-sm">
            <div className="p-4 border-b flex items-center gap-3">
              <AlertTriangle className="w-6 h-6 text-red-600" />
              <h2 className="text-lg font-semibold text-red-600">Maschinentyp deaktivieren?</h2>
            </div>
            <div className="p-4">
              <p className="text-slate-600">
                <strong>"{machineType?.name}"</strong> ({machineType?.code}) wird deaktiviert.
              </p>
              <p className="text-sm text-slate-500 mt-2">
                Der Typ kann nicht mehr für neue Maschinen verwendet werden.
              </p>
            </div>
            <div className="flex justify-end gap-2 p-4 border-t bg-slate-50 rounded-b-xl">
              <button
                onClick={() => setDeleteConfirmOpen(false)}
                className="px-4 py-2 border border-slate-300 rounded-lg hover:bg-slate-100"
              >
                Abbrechen
              </button>
              <button
                onClick={handleDelete}
                disabled={deleting}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50"
              >
                {deleting ? 'Deaktivieren...' : 'Deaktivieren'}
              </button>
            </div>
          </div>
        </div>
      )}
    </AdminLayout>
  );
}
